/**
 * useElectionProductSelector Composable
 *
 * Manages election product selection and automatic API data fetching.
 * Provides complete dialog data by fetching from contract market API.
 */
import { ref, computed, watch, toValue, type Ref, type ComputedRef, type MaybeRefOrGetter } from 'vue';
import type {
  ForecastTraderElectionMapProduct,
  ForecastTraderElectionTopOIResponse,
} from '@/types/forecasttrader.types';
import type {
  ElectionContract,
  PartyCode,
  USStateCode,
  ActualCandidates,
  ActualCandidate,
} from '../types/election.types';
import { STATE_NAMES } from '../types/election.types';
import { useGetElectionTopOI } from '@/api/forecasttrader.queries';

// Return type for the composable
export interface UseElectionProductSelectorReturn {
  /** Currently selected product */
  selectedProduct: Ref<ForecastTraderElectionMapProduct | null>;
  /** List of all available products for dropdown */
  availableProducts: ComputedRef<ForecastTraderElectionMapProduct[]>;
  /** Raw top-OI data from API (now the primary data source) */
  topOIData: ComputedRef<ForecastTraderElectionTopOIResponse | undefined>;
  /** Loading state for contract data fetching */
  isLoadingContracts: ComputedRef<boolean>;
  /** Error state for contract data fetching */
  contractError: ComputedRef<Error | null>;
  /** Derived election contract data for dialog display */
  electionContract: ComputedRef<ElectionContract | null>;
  /** Actual candidates based on strike_label data */
  actualCandidates: ComputedRef<ActualCandidates | null>;
  /**
   * All product conids associated with this race across exchanges:
   * the selected (FORECASTX) product conid plus every `product_conid`
   * found on `contract.matched[]`. Drives multi-exchange activity widgets.
   */
  productConids: ComputedRef<number[]>;
  /** Market information derived from API response */
  marketInfo: ComputedRef<{
    name: string;
    symbol: string;
    exchange: string;
    logoCategory: string;
    excludeHistoricalData: boolean;
  } | null>;
}

/**
 * Composable for managing election product selection and data fetching
 *
 * @param products - Reactive array of available election products
 * @example
 * ```vue
 * <script setup>
 * const {
 *   selectedProduct,
 *   availableProducts,
 *   electionContract,
 *   actualCandidates,
 *   isLoadingContracts
 * } = useElectionProductSelector(products)
 * </script>
 * ```
 */
export function useElectionProductSelector(
  products: MaybeRefOrGetter<ForecastTraderElectionMapProduct[]>,
  initialProductConid?: MaybeRefOrGetter<number | undefined>,
): UseElectionProductSelectorReturn {
  // Product selection state
  const selectedProduct = ref<ForecastTraderElectionMapProduct | null>(null);

  // Auto-select product on initialization or when products/initialProductConid change.
  // When initialProductConid changes (e.g. user clicks a different race), honour it
  // even if the current selection is still valid within the products list.
  watch(
    () => ({ products: toValue(products), initial: initialProductConid ? toValue(initialProductConid) : undefined }),
    ({ products: newProducts, initial }, oldValue) => {
      // Handle empty products array
      if (!newProducts || newProducts.length === 0) {
        selectedProduct.value = null;
        return;
      }

      // If initialProductConid changed, always try to honour it
      const initialChanged = initial !== undefined && initial !== oldValue?.initial;
      if (initialChanged) {
        const initialMatch = newProducts.find((p) => p.conid === initial);
        if (initialMatch) {
          selectedProduct.value = initialMatch;
          return;
        }
      }

      // Auto-select first product if none selected or current selection is not in new products
      const currentSelection = selectedProduct.value;
      const isCurrentSelectionValid = currentSelection && newProducts.some((p) => p.conid === currentSelection.conid);

      if (!isCurrentSelectionValid) {
        const initialMatch = initial ? newProducts.find((p) => p.conid === initial) : undefined;
        selectedProduct.value = initialMatch || newProducts[0];
      }
    },
    { immediate: true },
  );

  // Available products for dropdown
  const availableProducts = computed(() => [...(toValue(products) || [])].sort((a, b) => a.name.localeCompare(b.name)));

  // Fetch top contracts ordered by open interest - now the primary data source
  const {
    data: topOIData,
    isLoading: isLoadingContracts,
    error: contractError,
  } = useGetElectionTopOI(
    computed(() => {
      const conid = selectedProduct.value?.conid;
      return conid ? { productConid: conid } : { productConid: 0 };
    }),
  );

  /**
   * Transform API response into ElectionContract format
   */
  const electionContract = computed<ElectionContract | null>(() => {
    const data = topOIData.value;
    if (!data || !selectedProduct.value || !data.contracts || data.contracts.length === 0) return null;

    try {
      // Use response-level party field for the leading party
      const favorite = mapPartyStringToCode(data.party ?? '');

      // Use state from API response (parent level) — never parse market_name.
      // Falls back to 'DC' when the API omits the state field entirely.
      const stateCode = (data.state ? mapStateNameToCode(data.state) : undefined) ?? 'DC';

      const openInterestValue = data.open_interest ?? null;

      return {
        productConid: selectedProduct.value?.conid || 0,
        stateCode,
        name: data.market_name || 'Unknown Election',
        favorite,
        price: 0, // Placeholder - individual components fetch their own ask prices
        volume: '$0', // Placeholder volume data
        openInterest: openInterestValue ?? 0,
        district: extractDistrictFromMarketName(data.market_name || ''),
      };
    } catch (error) {
      console.warn('Error transforming election contract data:', error);
      return null;
    }
  });

  /**
   * Transform API response into ActualCandidates format
   * Now includes conid for each candidate so components can fetch their own ask prices
   */
  const actualCandidates = computed<ActualCandidates | null>(() => {
    const data = topOIData.value;
    if (!data || !data.contracts) return null;

    try {
      // Create actual candidates from each top-oi contract
      const candidates: ActualCandidate[] = data.contracts.map((contract) => {
        const party = mapPartyStringToCode(contract.party_affiliation ?? '');

        let exchanges: string[] | undefined;
        let yesConidExPairs: string[] | undefined;
        let noConidExPairs: string[] | undefined;
        if (contract.matched?.length) {
          exchanges = [contract.exchange, ...contract.matched.map((m) => m.exchange)];
          yesConidExPairs = [
            `${contract.conid_yes}@${contract.exchange}`,
            ...contract.matched.map((m) => `${m.conid_yes}@${m.exchange}`),
          ];
          noConidExPairs = [
            `${contract.conid_no}@${contract.exchange}`,
            ...contract.matched.map((m) => `${m.conid_no}@${m.exchange}`),
          ];
        }

        return {
          name: contract.strike_label || '',
          party,
          partyAffiliation: contract.party_affiliation || 'Independent',
          price: 0, // Placeholder - components fetch real bid prices via bid-ask queries
          conid: contract.conid_yes,
          conid_no: contract.conid_no,
          exchange: contract.exchange,
          exchanges,
          yesConidExPairs,
          noConidExPairs,
          side: 'Y',
          strike_label: contract.strike_label || '',
          photo: generateActualCandidatePhoto(party, contract.conid_yes),
          detail: `Candidate in ${data.market_name || 'Unknown Election'}`,
          isIncumbent: contract.incumbent ?? false,
          isLoadingPrice: false,
          usingAskPrice: false,
        };
      });

      // Candidates are already sorted by open interest (top-oi provides them in order)
      // No additional sorting needed - this preserves the order exactly as provided by top-oi
      const sortedCandidates = candidates;
      const leading = sortedCandidates[0];

      return {
        candidates: sortedCandidates,
        leading,
      };
    } catch (error) {
      console.warn('Error transforming actual candidate data:', error);
      return null;
    }
  });

  // Every product conid for this race across exchanges. Selected product's
  // conid is on FORECASTX; matched entries carry the sibling product conid
  // on each other exchange (e.g. KALSHI). De-duplicated, stable order:
  // selected product first, then matched in encounter order.
  const productConids = computed<number[]>(() => {
    const ids: number[] = [];
    const seen = new Set<number>();
    const push = (id: number | undefined) => {
      if (id && !seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    };

    push(selectedProduct.value?.conid);

    for (const contract of topOIData.value?.contracts ?? []) {
      for (const m of contract.matched ?? []) {
        push(m.product_conid);
      }
    }

    return ids;
  });

  /**
   * Market information from API response
   */
  const marketInfo = computed(() => {
    const data = topOIData.value;
    if (!data) return null;

    return {
      name: data.market_name,
      symbol: 'UNKNOWN', // Top-oi doesn't provide symbol
      exchange: data.contracts?.[0]?.exchange || 'FORECASTX', // Use exchange from first contract
      logoCategory: 'election', // Default for election markets
      excludeHistoricalData: false, // Default value
    };
  });

  return {
    selectedProduct,
    availableProducts,
    topOIData: computed(() => topOIData.value), // Now using topOI as primary data source
    isLoadingContracts: computed(() => isLoadingContracts.value),
    contractError: computed(() => contractError.value),
    electionContract,
    actualCandidates,
    productConids,
    marketInfo,
  };
}

// Helper functions for data transformation

/**
 * Map API party string (e.g., "Democrat", "Republican") to internal PartyCode
 */
function mapPartyStringToCode(party: string): PartyCode {
  const normalized = party.toLowerCase();
  if (normalized.includes('democrat')) return 'DEM';
  if (normalized.includes('republican')) return 'GOP';
  if (normalized.includes('green')) return 'GRN';
  if (normalized.includes('independent') || normalized.includes('nonpartisan')) return 'IND';
  if (party) console.warn(`Unknown party value: "${party}", defaulting to IND`);
  return 'IND';
}

/**
 * Map a state name (e.g., "Texas") to its USStateCode (e.g., "TX")
 * using the canonical STATE_NAMES lookup.
 * Returns undefined when the name doesn't match any known state.
 */
export function mapStateNameToCode(stateName: string): USStateCode | undefined {
  const normalized = stateName.trim();
  for (const [code, name] of Object.entries(STATE_NAMES)) {
    if (name === normalized) {
      return code as USStateCode;
    }
  }
  console.warn(`Could not map state name "${stateName}" to state code`);
  return undefined;
}

/**
 * Extract district from market name if it's a House race
 */
function extractDistrictFromMarketName(marketName: string): string | undefined {
  const districtMatch = marketName.match(/district\s+(\d+)/i);
  return districtMatch ? districtMatch[1] : undefined;
}

/**
 * Generate photo URL for actual candidates using conid-based forecastx service
 */
function generateActualCandidatePhoto(party: PartyCode, conid: number): string {
  // Check if we're in mock mode
  const isMockMode = import.meta.env.VITE_USE_MOCK !== 'false';

  if (isMockMode) {
    // Use local fallback images for mock mode
    const localImages = {
      DEM: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=200',
      GOP: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=200',
      IND: 'https://images.unsplash.com/photo-1540910419892-f0c07d391063?auto=format&fit=crop&q=80&w=200',
    };
    return localImages[party];
  }

  // Use simplified conid-based forecastx service for production mode
  return `https://api.ibkr.com/tws.proxy/public/icons/forecastx?conid=${conid}&fallback=true`;
}
